import { Link, Navigate, useNavigate } from 'react-router-dom';
import { t } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { Loading } from '../belege/Pages';
import { useShared } from '../belege/shared';
import { briefePath, letterCount } from '../briefe/labels';
import type { LettersResponse } from '../briefe/types';
import { useClientApp } from '../context';
import { jaPath } from '../jahresabschluss/labels';
import { usePage, type Crumb } from '../page';
import { docCount, dokumentePath, folderOf, FOLDERS, itemTitle, type FolderKey } from './labels';
import type { KanzleiDocItem, KanzleiDocsResponse } from './types';

// Bereich „Dokumente“ (Sam 11.10.): Firma wählen → Ordner der Firma → Dokumente eines Ordners. Briefe, die der Mandant
// selbst hochlädt, sind der Ordner „Briefe“ (eigene Seiten, nach Absender); Steuerbescheide, Dokumente der Kanzlei (im
// Ordner, den die Kanzlei beim Hochladen wählt) und BWAs zeigt die Ordnerseite; der Ordner „Jahresabschluss“ führt in
// den Bereich „Jahresabschluss“. Ein Dokument öffnet sich als Fenster (?dok=<id>, BWA ?bwa=<id>).

// Titel, Pfad und Zurück-Ziel: Start › Dokumente › <Firma> › <Ordner>. Mit nur einer Firma gibt es die Firmenwahl
// nicht, „Dokumente“ ist dann kein Link und Zurück springt von den Ordnern zur Übersicht. Titel und Ordner kommen übersetzt.
function useDokumentePage(p: { title: string; clientId?: string; clientName?: string; folder?: string }) {
  const { clients, clientName } = useClientApp();
  const one = clients.length === 1;
  const levels: Crumb[] = [
    { label: t('Start'), to: '/app' },
    { label: t('Dokumente'), to: one ? undefined : dokumentePath() },
  ];
  if (p.clientId) levels.push({ label: p.clientName ?? clientName(p.clientId), to: dokumentePath(p.clientId) });
  if (p.clientId && p.folder) levels.push({ label: p.folder });
  const last = levels.length - 1;
  const crumbs = levels.map((c, i) => (i === last ? { label: c.label } : c));
  let back = levels[last - 1]?.to ?? '/app';
  if (last === 2 && one) back = '/app';
  usePage({ title: p.title, crumbs, back, nav: 'dokumente' });
}

// neu laden bei neuen Unterlagen, neuer BWA und gelesenen Meldungen („Neu“), wie die Karte auf der Übersicht
export const useKanzleiDocs = (clientId: string) =>
  useShared<KanzleiDocsResponse>(`/api/app/docs/${clientId}`, ['docs', 'guv', 'notifications', 'clients']);
export const useLetterList = (clientId: string) => useShared<LettersResponse>(`/api/app/briefe/${clientId}`, ['briefe']);

export const folderPath = (clientId: string, key: FolderKey) =>
  key === 'briefe' ? briefePath(clientId) : key === 'jahresabschluss' ? jaPath(clientId) : dokumentePath(clientId, key);

// Dokumente · Für welche Firma? (nur mit mehreren Firmen)
export function FirmsPage() {
  const { clients } = useClientApp();
  useDokumentePage({ title: t('Dokumente') });
  if (clients.length === 1) return <Navigate to={dokumentePath(clients[0]!.id)} replace />;
  return (
    <>
      <div className="hello">
        <h2>{t('Dokumente')}</h2>
        <p>{t('Für welche Firma?')}</p>
      </div>
      <div className="list">
        {clients.map((c) => (
          <Link key={c.id} className="item" to={dokumentePath(c.id)}>
            <span className="ico" aria-hidden="true">
              {c.name.charAt(0)}
            </span>
            <span className="tx">
              <b>{c.name}</b>
              <small>{t('Briefe, Steuerbescheide, Vollmachten & Co.')}</small>
            </span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}

// Dokumente · <Firma>: alle Ordner mit Anzahl, „neu“ und „zu unterschreiben“
export function OverviewPage({ clientId }: { clientId: string }) {
  const { clientName } = useClientApp();
  const docs = useKanzleiDocs(clientId);
  const letters = useLetterList(clientId);
  const name = clientName(clientId);
  useDokumentePage({ title: t('Dokumente'), clientId, clientName: name });
  const items = docs.data?.items ?? [];
  const ls = letters.data?.letters ?? [];

  return (
    <>
      <div className="hello">
        <h2>{t('Dokumente')}</h2>
        <p>
          {name} · {t('nach Ordnern sortiert')}
        </p>
      </div>
      {!docs.data || !letters.data ? (
        <Loading error={docs.error ?? letters.error} />
      ) : (
        <>
          <Link className="cta brief" to={`${briefePath(clientId)}?neu=1`}>
            <span>
              <b>✉ {t('Neuen Brief hochladen')}</b>
              <small>{t('Post von Ämtern, Krankenkasse, Bank & Co. · Foto oder PDF')}</small>
            </span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </Link>
          <div className="list dfolders">
            {FOLDERS.map((f) => {
              const inFolder = items.filter((x) => folderOf(x) === f.key);
              let sub: string;
              const badges: { text: string; cls: string }[] = [];
              if (f.key === 'briefe') {
                const open = ls.filter((l) => l.status === 'offen').length;
                const asks = ls.filter((l) => l.status === 'offen' && l.ask).length;
                sub = !ls.length
                  ? t('Briefe, die Sie hochladen')
                  : open
                    ? t('{letters} · {n} in Bearbeitung', { letters: letterCount(ls.length), n: open })
                    : letterCount(ls.length);
                if (asks) badges.push({ text: asks === 1 ? t('1 Rückfrage') : t('{n} Rückfragen', { n: asks }), cls: 'b-warn' });
              } else if (f.key === 'jahresabschluss') {
                sub = inFolder.length ? inFolder.map((x) => x.year).join(', ') : t('noch keiner');
              } else {
                sub = inFolder.length ? docCount(inFolder.length) : t('noch keine');
              }
              const toSign = inFolder.filter((x) => x.sign === 'offen').length;
              const fresh = inFolder.filter((x) => x.new && x.sign !== 'offen').length;
              if (toSign) badges.push({ text: t('{n} zu unterschreiben', { n: toSign }), cls: 'b-warn' });
              if (fresh) badges.push({ text: t('{n} neu', { n: fresh }), cls: 'b-ok' });
              return (
                <Link key={f.key} className="item" to={folderPath(clientId, f.key)}>
                  <span className="ico" aria-hidden="true">
                    {f.ico}
                  </span>
                  <span className="tx">
                    <b>{t(f.label)}</b>
                    <small>{sub}</small>
                    {badges.map((b) => (
                      <span key={b.text} className={`badge ${b.cls}`}>
                        {b.text}
                      </span>
                    ))}
                  </span>
                  <span className="chev" aria-hidden="true">
                    ›
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="src">{t('Briefe laden Sie selbst hoch. Alles andere legt Ihre Kanzlei hier für Sie ab.')}</div>
        </>
      )}
    </>
  );
}

// Dokument oder BWA als Fenster über der Seite öffnen (eigener Verlaufseintrag, so schließt auch die Zurück-Geste)
export function useOpenDoc() {
  const navigate = useNavigate();
  return (x: KanzleiDocItem) =>
    navigate({ search: x.type === 'bwa' ? `?bwa=${x.id}` : `?dok=${x.id}` }, { state: { sheet: true } });
}

// <Ordner> · Dokumente des Ordners, neueste zuerst
export function FolderPage({ clientId, folder }: { clientId: string; folder: { key: FolderKey; label: string } }) {
  const { clientName } = useClientApp();
  const docs = useKanzleiDocs(clientId);
  const open = useOpenDoc();
  const name = clientName(clientId);
  const label = t(folder.label);
  useDokumentePage({ title: label, clientId, clientName: name, folder: label });
  const items = (docs.data?.items ?? []).filter((x) => folderOf(x) === folder.key);

  return (
    <>
      <div className="hello">
        <h2>{label}</h2>
        <p>
          {t('Dokumente')} · {name}
        </p>
      </div>
      {!docs.data ? (
        <Loading error={docs.error} />
      ) : (
        <div className="files dfiles">
          <h3>
            {t('Von Ihrer Kanzlei')} ({items.length})
          </h3>
          {items.map((x) => (
            <button key={x.id} type="button" className="file" onClick={() => open(x)}>
              <span>
                {itemTitle(x)}
                <small className="fsub">{t('hochgeladen am {date}', { date: fmtDate(x.uploadedAt) })}</small>
              </span>
              {x.sign === 'offen' ? (
                <span className="wait">{t('Bitte unterschreiben')}</span>
              ) : x.sign === 'unterschrieben' ? (
                <span className="ok">{t('unterschrieben')} ✓</span>
              ) : x.new ? (
                <span className="wait">{t('Neu')}</span>
              ) : (
                <span className="ok">{t('Ansehen')}</span>
              )}
            </button>
          ))}
          {!items.length && <div className="empty">{t('Noch nichts in diesem Ordner.')}</div>}
        </div>
      )}
    </>
  );
}
